import { AuthProvider as SupabaseAuthProvider, useSupabaseAuth } from "@/auth/AuthProvider";
import { supabase } from "@/lib/supabaseClient";
import { Session, User as SupabaseUser } from "@supabase/supabase-js";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

type Role = "owner" | "staff" | "client";

interface Profile {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: Role;
  approved: boolean;
  adminSlug: string | null;
  createdAt: string | null;
}

interface SignUpProfile {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phone?: string;
}

interface SignUpResult {
  user: SupabaseUser | null;
  session: Session | null;
  needsEmailConfirmation: boolean;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  profileLoading: boolean;
  profileError: string | null;
  isAdmin: boolean;
  isPending: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  signUp?: (
    email: string,
    password: string,
    profile?: SignUpProfile,
  ) => Promise<SignUpResult>;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  adminSlug: string | null;
  adminSlugLoading: boolean;
  adminSlugError: string | null;
  getAdminSlug: () => Promise<string>;
  refreshProfile: () => Promise<Profile>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const mapSupabaseUser = (supabaseUser: SupabaseUser | null): User | null => {
  if (!supabaseUser) return null;
  const nameFromEmail = supabaseUser.email?.split("@")[0] ?? "Usuário";
  const fullName =
    (supabaseUser.user_metadata as Record<string, string | undefined> | null)
      ?.full_name ?? nameFromEmail;
  const avatarUrl =
    (supabaseUser.user_metadata as Record<string, string | undefined> | null)
      ?.avatar_url ??
    `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=6366f1&color=fff`;

  return {
    id: supabaseUser.id,
    name: fullName,
    email: supabaseUser.email ?? "",
    avatar: avatarUrl,
  };
};

const AuthAdapter = ({ children }: { children: ReactNode }) => {
  const {
    user: supabaseUser,
    loading: authLoading,
    error,
    signIn,
    signUp: supabaseSignUp,
    signOut,
  } = useSupabaseAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const mapProfileRow = useCallback(
    (row: {
      id: string;
      email: string | null;
      first_name: string | null;
      last_name: string | null;
      phone: string | null;
      role: string | null;
      approved: boolean | null;
      admin_slug: string | null;
      created_at: string | null;
    }): Profile => ({
      id: row.id,
      email: row.email,
      firstName: row.first_name,
      lastName: row.last_name,
      phone: row.phone,
      role: (row.role as Role) ?? "client",
      approved: !!row.approved,
      adminSlug: row.admin_slug,
      createdAt: row.created_at,
    }),
    []
  );

  const fetchProfile = useCallback(
    async (userId: string) => {
      setProfileLoading(true);
      setProfileError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from("profiles")
          .select("id,email,first_name,last_name,phone,role,approved,admin_slug,created_at")
          .eq("id", userId)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          throw new Error("Perfil não encontrado para este usuário.");
        }

        const mapped = mapProfileRow(data);
        setProfile(mapped);
        return mapped;
      } catch (err) {
        const message = (err as Error)?.message ?? "Falha ao obter perfil.";
        console.error("[auth] erro ao buscar profile", err);
        setProfileError(message);
        setProfile(null);
        throw err;
      } finally {
        setProfileLoading(false);
      }
    },
    [mapProfileRow]
  );

  const refreshProfile = useCallback(async () => {
    if (!supabaseUser?.id) {
      throw new Error("Usuário não autenticado.");
    }
    return fetchProfile(supabaseUser.id);
  }, [fetchProfile, supabaseUser?.id]);

  const getAdminSlug = useCallback(async () => {
    if (profile?.adminSlug) return profile.adminSlug;
    const updatedProfile = await refreshProfile();
    if (updatedProfile.adminSlug) return updatedProfile.adminSlug;
    throw new Error("admin_slug não encontrado para este usuário.");
  }, [profile?.adminSlug, refreshProfile]);

  useEffect(() => {
    if (!supabaseUser?.id) {
      setProfile(null);
      setProfileError(null);
      setProfileLoading(false);
      return;
    }

    fetchProfile(supabaseUser.id).catch(() => {
      // erro já tratado em fetchProfile; manter estado.
    });
  }, [supabaseUser?.id, fetchProfile]);

  const isAdmin = profile?.role === "owner" || profile?.role === "staff";
  const isPending = !!supabaseUser && !!profile && !profile.approved;
  const adminSlug = profile?.adminSlug ?? null;
  const adminSlugLoading = profileLoading;
  const adminSlugError = profileError;

  const value = useMemo<AuthContextType>(
    () => ({
      user: mapSupabaseUser(supabaseUser),
      profile,
      profileLoading,
      profileError,
      isAdmin,
      isPending,
      isAuthenticated: !!supabaseUser,
      loading: authLoading || profileLoading,
      error,
      adminSlug,
      adminSlugLoading,
      adminSlugError,
      getAdminSlug,
      refreshProfile,
      login: async (email: string, password: string) => {
        await signIn(email, password);
        return true;
      },
      signUp: async (
        email: string,
        password: string,
        profile?: SignUpProfile,
      ): Promise<SignUpResult> => supabaseSignUp(email, password, profile),
      logout: async () => {
        await signOut();
      },
    }),
    [
      supabaseUser,
      profile,
      profileLoading,
      profileError,
      isAdmin,
      isPending,
      authLoading,
      error,
      adminSlug,
      adminSlugLoading,
      adminSlugError,
      getAdminSlug,
      refreshProfile,
      signIn,
      supabaseSignUp,
      signOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => (
  <SupabaseAuthProvider>
    <AuthAdapter>{children}</AuthAdapter>
  </SupabaseAuthProvider>
);